import { useSyncExternalStore } from "react"

const navigationEvent = "resolto:navigation"

function subscribe(listener: () => void): () => void {
  window.addEventListener("popstate", listener)
  window.addEventListener(navigationEvent, listener)
  return () => {
    window.removeEventListener("popstate", listener)
    window.removeEventListener(navigationEvent, listener)
  }
}

function snapshot(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function navigate(href: string, replace = false): void {
  const target = new URL(href, window.location.href)
  if (target.origin !== window.location.origin) {
    window.location.assign(target)
    return
  }
  const current = snapshot()
  const next = `${target.pathname}${target.search}${target.hash}`
  if (current !== next) {
    if (replace) window.history.replaceState({}, "", next)
    else window.history.pushState({}, "", next)
  }
  window.dispatchEvent(new Event(navigationEvent))
  if (target.hash) {
    window.requestAnimationFrame(() => document.querySelector(target.hash)?.scrollIntoView())
  } else {
    window.scrollTo({ top: 0, behavior: "instant" })
  }
}

export function useLocation(): string {
  return useSyncExternalStore(subscribe, snapshot, () => "/")
}

export function usePathname(): string {
  return useLocation().split(/[?#]/, 1)[0] ?? "/"
}

export function useRouter() {
  return {
    push: (href: string) => navigate(href),
    replace: (href: string) => navigate(href, true),
    back: () => window.history.back(),
    refresh: () => window.dispatchEvent(new Event(navigationEvent)),
  }
}

export function notFound(): never {
  throw new Error("RESOLTO_NOT_FOUND")
}

export { navigate }
